/**
 * useCmsMediaIndex — media-library index (uuid → URLs + alt + dimensions),
 * fresher than the bundle.
 *
 * The build-time snapshot (`media.generated.ts`) seeds state so image rungs
 * resolve on first paint; `/api/cms/public/media-index` replaces it on mount
 * so a newly uploaded image is usable without a rebuild.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cmsMediaIndex, type CmsMediaIndexEntry } from '@/content/cms/media.generated'
import { previewTokenParam } from '@/utils/previewToken'
import { DEFAULT_LOCALE, type Locale } from '@/utils/localePath'
import { useCmsData } from '@/cms/CmsDataContext'

interface MediaIndexResp {
  media?: Record<string, CmsMediaIndexEntry>
}

export function useCmsMediaIndex(): Record<string, CmsMediaIndexEntry> {
  const { i18n } = useTranslation()
  const locale = (i18n.language || DEFAULT_LOCALE) as Locale
  const cmsData = useCmsData()
  const initial = cmsData?.mediaIndex ?? cmsMediaIndex
  const [index, setIndex] = useState<Record<string, CmsMediaIndexEntry>>(initial)

  useEffect(() => {
    if (cmsData?.mediaIndex) return
    const controller = new AbortController()
    let cancelled = false

    void fetch(
      `/api/cms/public/media-index${previewTokenParam()}`,
      { signal: controller.signal, credentials: 'same-origin' },
    )
      .then((r) => (r.ok ? (r.json() as Promise<MediaIndexResp>) : null))
      .then((j) => {
        if (cancelled) return
        if (!j?.media || Object.keys(j.media).length === 0) return
        setIndex(j.media)
      })
      .catch(() => {
        // Network error / abort — keep the snapshot seed.
      })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [locale, cmsData?.mediaIndex])

  return index
}
